import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "src/components/ui/card";
import { Button } from "src/components/ui/button";
import { BookOpen, FileText, Video, Clock, BookmarkCheck, Bookmark, Loader2, Users } from "lucide-react";
import LoadingBar from '../components/ui/LoadingBar';
import './PracticeMaterials.css';
import { fetchWithAuth } from '../utils/api';
import { supabase, BUCKET_NAME, MATERIALS_FOLDER, createSignedUrl } from '../config/supabase';
import toast from 'react-hot-toast';

// Define types for materials
interface Material {
  id: string;
  title: string;
  description: string;
  type: 'video' | 'article' | 'exercise' | 'template';
  level: 'beginner' | 'intermediate' | 'advanced';
  duration?: string;
  tags: string[];
  url: string;
  file_name?: string;
  file_path?: string;
}

/**
 * Generate a properly signed URL using Supabase's createSignedUrl method
 * This creates a URL with the format:
 * https://[project].supabase.co/storage/v1/object/sign/[bucket]/[filename]?token=[token]
 */
async function createProperSignedUrl(filename: string): Promise<string | null> {
  try {
    console.log(`Attempting to generate signed URL for file: ${filename}`);
    
    // Try to extract just the filename without any path information
    let cleanFilename = filename;
    
    // Handle paths that might include the bucket name
    if (cleanFilename.includes(`${BUCKET_NAME}/`)) {
      cleanFilename = cleanFilename.split(`${BUCKET_NAME}/`)[1];
      console.log(`Extracted path from bucket path: ${cleanFilename}`);
    } else {
      cleanFilename = filename.split('/').pop() || filename;
      console.log(`Using clean filename: ${cleanFilename}`);
    }
    
    // Use the helper function from config
    const signedUrl = await createSignedUrl(BUCKET_NAME, MATERIALS_FOLDER, cleanFilename);
    
    if (!signedUrl) {
      console.error('Failed to create signed URL');
      return null;
    }
    
    // Log the signed URL for debugging
    console.log("Signed URL details:", {
      url: signedUrl,
      hasToken: signedUrl.includes('token='),
      expires: new Date(Date.now() + 86400 * 1000).toLocaleString()
    });
    
    return signedUrl;
  } catch (err) {
    console.error('Exception creating signed URL:', err);
    return null;
  }
}

// Function to open material with proper signed URLs
const openMaterial = async (material: Material) => {
  console.log("Opening material:", material);
  
  try {
    toast.loading("Preparing resource...", { id: "loading-resource" });
    
    // Direct external URL handling
    if (material.url.startsWith('http') && !material.url.includes('supabase')) {
      window.open(material.url, '_blank');
      toast.success("Resource opened in new tab");
      toast.dismiss("loading-resource");
      return;
    }
    
    // Get the most reliable file information
    let filename = material.file_path || material.file_name || '';
    
    // If we have no filename but URL looks like a path, use that
    if (!filename && material.url && !material.url.startsWith('http')) {
      filename = material.url;
    }
    
    if (!filename) {
      toast.error("File information is missing");
      toast.dismiss("loading-resource");
      return;
    }
    
    // Generate a properly signed URL with token
    const signedUrl = await createProperSignedUrl(filename);
    
    if (signedUrl) {
      // Open in new tab or download
      window.open(signedUrl, '_blank');
      toast.success("Resource opened in new tab");
    } else {
      toast.error("Could not generate secure access link");
    }
  } catch (error) {
    console.error("Error accessing material:", error);
    toast.error("Failed to access resource");
  } finally {
    toast.dismiss("loading-resource");
  }
};

const PracticeMaterials: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('All Resources');
  const [selectedLevel, setSelectedLevel] = useState('All Levels');
  const [materials, setMaterials] = useState<Material[]>([]);
  const [savedMaterials, setSavedMaterials] = useState<string[]>([]);
  const [savingMaterial, setSavingMaterial] = useState<string | null>(null);
  const [loadingMaterialAction, setLoadingMaterialAction] = useState<{id: string, action: 'open' | 'download'} | null>(null);

  useEffect(() => {
    fetchMaterials();
    // Load saved materials from localStorage
    const savedItems = localStorage.getItem('savedMaterials');
    if (savedItems) {
      setSavedMaterials(JSON.parse(savedItems));
    }
  }, []);

  const fetchMaterials = async () => {
    try {
      setIsLoading(true);
      // Fetch materials from the backend
      const response = await fetchWithAuth('/materials/', {}, 0, false);
      
      if (response) {
        setMaterials(response.materials);
      } else {
        // Handle empty response
        setMaterials([]);
        toast.error("Could not load materials. Please try again later.");
      }
    } catch (error) {
      console.error('Error fetching materials:', error);
      setMaterials([]);
      toast.error("Failed to load materials. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle saving a material to favorites
  const handleSaveMaterial = async (material: Material) => {
    try {
      setSavingMaterial(material.id);
      
      // In a real app, we would save to backend
      // For now, we'll just save to localStorage
      const updatedSaved = savedMaterials.includes(material.id)
        ? savedMaterials.filter(id => id !== material.id) // Remove if exists
        : [...savedMaterials, material.id]; // Add if doesn't exist
        
      setSavedMaterials(updatedSaved);
      localStorage.setItem('savedMaterials', JSON.stringify(updatedSaved));
      
      // Show toast notification
      if (savedMaterials.includes(material.id)) {
        toast.success("Material removed from saved items");
      } else {
        toast.success("Material saved successfully");
      }
      
    } catch (error) {
      console.error('Error saving material:', error);
      toast.error("Failed to save material");
    } finally {
      setSavingMaterial(null);
    }
  };

  // Handle opening a material with loading state
  const handleOpenMaterial = async (material: Material) => {
    setLoadingMaterialAction({ id: material.id, action: 'open' });
    await openMaterial(material);
    setLoadingMaterialAction(null);
  };

  // Filter materials based on search, type, and level
  const filteredMaterials = materials.filter(material => {
    const matchesSearch = material.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        material.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        material.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesType = selectedType === 'All Resources' || 
                        material.type === selectedType.toLowerCase().slice(0, -1);
    
    const matchesLevel = selectedLevel === 'All Levels' || 
                          material.level === selectedLevel.toLowerCase();
    
    return matchesSearch && matchesType && matchesLevel;
  });

  // Function to get appropriate icon based on material type
  const getIcon = (type: string) => {
    switch(type) {
      case 'video': return <Video />;
      case 'article': return <FileText />;
      case 'exercise': return <Users />;
      case 'template': return <BookOpen />;
      default: return <FileText />;
    }
  };

  return (
    <div className="practice-materials-page">
      <LoadingBar isLoading={isLoading} />
      
      <h1 className="page-title">Practice Materials</h1>
      
      <div className="page-description">
        <p>Improve your debate skills with our curated collection of resources, exercises, and templates.</p>
      </div>
      
      {/* Search Bar */}
      <div className="resource-search">
        <input
          type="text"
          className="search-input"
          placeholder="Search resources..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      
      {/* Resource Type Tabs */}
      <div className="resource-tabs">
        {['All Resources', 'Videos', 'Articles', 'Exercises', 'Templates'].map(type => (
          <button
            key={type}
            className={`tab-button ${selectedType === type ? 'active' : ''}`}
            onClick={() => setSelectedType(type)}
          >
            {type}
          </button>
        ))}
      </div>
      
      {/* Level Filters */}
      <div className="resource-filters">
        <span className="filter-label">Filter by level:</span>
        <div className="level-filters">
          {['All Levels', 'Beginner', 'Intermediate', 'Advanced'].map(level => (
            <button
              key={level}
              className={`level-button ${level.toLowerCase() !== 'all levels' ? level.toLowerCase() : ''} ${selectedLevel === level ? 'active' : ''}`}
              onClick={() => setSelectedLevel(level)}
            >
              {level}
            </button>
          ))}
        </div>
            </div>
      
      {/* Materials Grid */}
      <div className="resources-grid">
        {filteredMaterials.length > 0 ? (
          filteredMaterials.map((material) => (
            <Card key={material.id} className={`resource-card ${material.type}`}>
              <div className="resource-type-icon">
                {getIcon(material.type)}
            </div>
              <div className="resource-content">
                <h3 className="resource-title">{material.title}</h3>
                <p className="resource-description">{material.description}</p>
                
                <div className="resource-meta">
                  <span className={`resource-level ${material.level}`}>
                    {material.level.charAt(0).toUpperCase() + material.level.slice(1)}
                  </span>
                  
                  {material.duration && (
                    <span className="resource-duration">
                      <Clock size={14} />
                      {material.duration}
                    </span>
                  )}
            </div>
                
                <div className="resource-tags">
                  {material.tags.map((tag, index) => (
                    <span key={index} className="resource-tag">#{tag}</span>
                  ))}
            </div>
                
                <div className="resource-actions">
                  {material.type === 'video' && (
                    <Button 
                      variant="default" 
                      onClick={() => handleOpenMaterial(material)}
                      disabled={loadingMaterialAction?.id === material.id}
                    >
                      {loadingMaterialAction?.id === material.id && loadingMaterialAction.action === 'open' ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Video size={16} className="mr-2" />
                      )}
                      Watch Now
                    </Button>
                  )}
                  {material.type === 'article' && (
                    <Button 
                      variant="default" 
                      onClick={() => handleOpenMaterial(material)}
                      disabled={loadingMaterialAction?.id === material.id}
                    >
                      {loadingMaterialAction?.id === material.id && loadingMaterialAction.action === 'open' ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <FileText size={16} className="mr-2" />
                      )}
                      Open Resource
                    </Button>
                  )}
                  {material.type === 'exercise' && (
                    <Button 
                      variant="default" 
                      onClick={() => handleOpenMaterial(material)}
                      disabled={loadingMaterialAction?.id === material.id}
                    >
                      {loadingMaterialAction?.id === material.id && loadingMaterialAction.action === 'open' ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Users size={16} className="mr-2" />
                      )}
                      Start Exercise
                    </Button>
                  )}
                  {material.type === 'template' && (
                    <Button 
                      variant="default" 
                      onClick={() => handleOpenMaterial(material)}
                      disabled={loadingMaterialAction?.id === material.id}
                    >
                      {loadingMaterialAction?.id === material.id && loadingMaterialAction.action === 'open' ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <BookOpen size={16} className="mr-2" />
                      )}
                      Open Template
                    </Button>
                  )}
                  
                  {/* Save button */}
                  <Button 
                    variant="outline" 
                    onClick={() => handleSaveMaterial(material)}
                    disabled={savingMaterial === material.id}
                    className="ml-1"
                  >
                    {savedMaterials.includes(material.id) ? (
                      <>
                        <BookmarkCheck size={16} className="mr-1" />
                        Saved
                      </>
                    ) : (
                      <>
                        <Bookmark size={16} className="mr-1" />
                        Save
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          ))
        ) : (
          <div className="no-materials-message">
            <FileText size={48} className="no-materials-icon" />
            <h3>No materials found</h3>
            {searchQuery || selectedType !== 'All Resources' || selectedLevel !== 'All Levels' ? (
              <>
                <p>Try adjusting your search criteria or filters</p>
                <Button 
                  variant="default" 
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedType('All Resources');
                    setSelectedLevel('All Levels');
                  }}
                >
                  Clear Filters
                </Button>
              </>
            ) : (
              <p>No practice materials are currently available</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PracticeMaterials; 